/**
 * useDicomSeriesLoader - DICOM Loading Hook
 *
 * Loads DICOM images from S3 storage via backend proxy.
 * Uses 'wadouri:' scheme for standard DICOM loading.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAtomValue, useSetAtom, getDefaultStore } from "jotai";

import { getBaseFilename, buildFilenameToIndexMapFromMetadata } from "../utils/fileHelpers";
import type { DicomSeriesData, UseSeriesLoaderProps } from "../components/types";
import { initializeCornerstone } from "../utils/dicomLoader";
import {
  buildS3ImageIds,
  StudyNotCanonicalizedError,
  pickPreferredSeries,
} from "../utils/dicomMetadataLoader";
import { useStudyMetadata } from "./useStudyQueries";
import { perfMark, perfMeasure, PERF_EVENTS } from "../utils/performanceTiming";
import {
  currentSliceAtom,
  loadedSeriesAtom,
  selectedSeriesIdAtom,
  seriesErrorAtom,
  seriesLoadingStateAtom,
  studyIsLoadingAtom,
  studyMetadataAtom,
  totalSlicesAtom,
} from "../atoms/selectionAtoms";

/**
 * Loading stage for progressive UI feedback
 */
export type LoadingStage =
  | "fetching-metadata"
  | "initializing-cornerstone"
  | "loading-frames"
  | "ready";

export function useDicomSeriesLoader({
  studyId,
  modelSelectedSeriesId,
  userId,
  token,
  preferredSeriesId = null,
  imagesUrl,
}: UseSeriesLoaderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [cornerstoneError, setCornerstoneError] = useState<string | null>(null);

  // Performance timing refs
  const timingStartedRef = useRef(false);
  const metadataTimedRef = useRef(false);

  // Loading state — atom-backed. selectedSeriesIdAtom is the SSoT for the
  // active series; loadedSeriesAtom holds the DICOM data the loader has
  // built imageIds for; the remaining atoms expose loading-state-machine
  // signals so the overlay + downstream consumers can render without
  // threading state through prop chains.
  const selectedSeriesId = useAtomValue(selectedSeriesIdAtom);
  const setSelectedSeriesId = useSetAtom(selectedSeriesIdAtom);
  const loadedSeries = useAtomValue(loadedSeriesAtom);
  const setLoadedSeries = useSetAtom(loadedSeriesAtom);
  const isStudyLoading = useAtomValue(studyIsLoadingAtom);
  const setStudyIsLoading = useSetAtom(studyIsLoadingAtom);
  const seriesLoadingState = useAtomValue(seriesLoadingStateAtom);
  const setSeriesLoadingState = useSetAtom(seriesLoadingStateAtom);
  const seriesError = useAtomValue(seriesErrorAtom);
  const setSeriesError = useSetAtom(seriesErrorAtom);

  // Slice-state writers — called inline at the handlers that change series
  // selection or load new dims (loadSeries, handleSeriesSelect, initial-load
  // effect below) so the slice atoms move in lockstep with their cause
  // instead of being synced via a derived-state useEffect bridge.
  const setTotalSlices = useSetAtom(totalSlicesAtom);
  const setCurrentSlice = useSetAtom(currentSliceAtom);

  // Mark start of loading on mount
  useEffect(() => {
    if (!timingStartedRef.current) {
      timingStartedRef.current = true;
      perfMark("study-load-start");
    }
  }, []);

  // Study metadata - fetched via TanStack Query (automatic deduplication & caching)
  const {
    data: studyMetadata,
    isLoading: isLoadingMetadata,
    error: metadataQueryError,
  } = useStudyMetadata(token, userId, studyId, { imagesUrl });

  // Time when metadata arrives
  useEffect(() => {
    if (studyMetadata && !metadataTimedRef.current) {
      metadataTimedRef.current = true;
      perfMeasure(PERF_EVENTS.STUDY_METADATA_LOAD, "study-load-start");
    }
  }, [studyMetadata]);

  // Mirror React Query's studyMetadata into the studyMetadataAtom SSoT.
  // Downstream instance-UID → slice-index lookups derive off this atom
  // (selectionAtoms.ts) so callers don't have to thread metadata through
  // props or re-walk it in their own effects.
  const setStudyMetadata = useSetAtom(studyMetadataAtom);
  useEffect(() => {
    setStudyMetadata(studyMetadata ?? null);
  }, [studyMetadata, setStudyMetadata]);

  // Canonical-only path. Studies that haven't been migrated cause
  // fetchStudyMetadata to throw StudyNotCanonicalizedError, which lands in
  // metadataQueryError; the CaseViewer renders its "Migration pending" panel
  // based on that, so we don't surface a separate error string here.
  const isDicomAvailable = !!studyMetadata;

  // Surface the "study not migrated" case as a structured flag so the
  // CaseViewer can render the dedicated panel without substring-matching.
  const studyNotCanonicalized = metadataQueryError instanceof StudyNotCanonicalizedError;

  // Derive error state from cornerstone, series, and metadata query errors.
  const metadataError = useMemo(() => {
    if (cornerstoneError) return cornerstoneError;
    if (seriesError) return seriesError;
    if (metadataQueryError) return metadataQueryError.message;
    return null;
  }, [cornerstoneError, seriesError, metadataQueryError]);

  // Initialize Cornerstone on mount
  useEffect(() => {
    const init = async () => {
      try {
        perfMark("cornerstone-init-start");
        await initializeCornerstone();
        perfMeasure("cornerstone-init", "cornerstone-init-start");
        setIsInitialized(true);
      } catch {
        setCornerstoneError("Failed to initialize DICOM viewer");
      }
    };
    init();
  }, []);

  /**
   * Load a series - displays immediately, preloads in background
   *
   * Loads DICOM via S3 mode. Logs timing metrics for performance analysis.
   */
  const loadSeries = useCallback(
    async (seriesId: string) => {
      // studyIsLoadingAtom gates concurrent calls — handleSeriesSelect at the
      // call site checks it before invoking loadSeries. Set true at the top
      // so a second call while the first is still resolving short-circuits.
      // Cleared in finally so an early return / thrown error doesn't strand
      // the atom at true.
      setStudyIsLoading(true);
      try {
        if (!isDicomAvailable || !studyMetadata?.series) {
          setSeriesError("Study not available");
          return;
        }

        const instances = studyMetadata.series[seriesId];
        if (!instances?.length) {
          setSeriesError(`Series ${seriesId} not found`);
          return;
        }

        // Build imageIds for canonical-bucket mode
        if (!studyMetadata.canonical_cdn_base_url) {
          setSeriesError("No canonical CDN base URL available");
          return;
        }

        const allImageIds = buildS3ImageIds(studyMetadata.canonical_cdn_base_url, instances);

        if (!allImageIds.length) {
          setSeriesError("No valid images found in series");
          return;
        }

        setSeriesError(null);

        // Build filename-to-index mapping from metadata
        const filenameToIndex = buildFilenameToIndexMapFromMetadata(instances);

        // Estimate dimensions from metadata (first instance)
        const firstInstance = instances[0];
        const estimatedDims: [number, number, number] = [
          allImageIds.length,
          firstInstance?.rows || 512,
          firstInstance?.columns || 512,
        ];

        setTotalSlices(estimatedDims[0]);

        setLoadedSeries((prev) => {
          const existing = prev.find((s) => getBaseFilename(s.filename) === seriesId);
          if (existing) {
            return prev.map((s) =>
              getBaseFilename(s.filename) === seriesId
                ? ({
                    ...s,
                    imageIds: allImageIds,
                    dims: estimatedDims,
                    filenameToIndex,
                  } as DicomSeriesData)
                : s,
            );
          }
          return [
            ...prev,
            {
              imageIds: allImageIds,
              dims: estimatedDims,
              filename: seriesId,
              studyId,
              predictions: {},
              filenameToIndex,
              metadata: undefined,
            } as DicomSeriesData,
          ];
        });
      } finally {
        setStudyIsLoading(false);
      }
    },
    [
      studyMetadata,
      isDicomAvailable,
      studyId,
      setLoadedSeries,
      setSeriesError,
      setStudyIsLoading,
      setTotalSlices,
    ],
  );

  // Combined effect: Auto-select AND load initial series when metadata is ready
  // This eliminates the race condition between separate select/load effects
  useEffect(() => {
    if (!isInitialized) return;
    if (!isDicomAvailable || !studyMetadata?.series) return;
    if (loadedSeries.length > 0) return; // Already loaded a series
    if (isStudyLoading) return;

    const availableSeries = Object.keys(studyMetadata.series);
    if (availableSeries.length === 0) return;

    // Determine which series to load: prefer model's selection if available.
    // preferredSeriesId comes from the caller's useModelPrediction (live data);
    // modelSelectedSeriesId is the prop fallback when no live prediction.
    // Handle compound format "studyUID_seriesUID" from device-server by extracting the series UID portion
    let resolvedModelSeriesId = preferredSeriesId ?? modelSelectedSeriesId ?? null;
    if (
      resolvedModelSeriesId &&
      !studyMetadata.series[resolvedModelSeriesId] &&
      resolvedModelSeriesId.includes("_")
    ) {
      const seriesPart = resolvedModelSeriesId.split("_").pop()!;
      if (studyMetadata.series[seriesPart]) {
        resolvedModelSeriesId = seriesPart;
      }
    }
    const seriesIdToLoad =
      resolvedModelSeriesId && studyMetadata.series[resolvedModelSeriesId]
        ? resolvedModelSeriesId
        : pickPreferredSeries(availableSeries, studyMetadata.series_metadata);
    // availableSeries.length > 0 is gated above, so pickPreferredSeries
    // can't return undefined here — narrow for the type checker.
    if (!seriesIdToLoad) return;

    // Set selectedSeriesId AND load in one go - no race condition.
    // currentSlice anchors to 0 for the freshly-selected series; loadSeries
    // writes totalSlices once dims resolve.
    setSelectedSeriesId(seriesIdToLoad);
    setCurrentSlice(0);

    // Load immediately - don't wait for another effect cycle
    loadSeries(seriesIdToLoad);
  }, [
    isInitialized,
    isDicomAvailable,
    studyMetadata,
    loadedSeries.length,
    isStudyLoading,
    modelSelectedSeriesId,
    preferredSeriesId,
    setSelectedSeriesId,
    setCurrentSlice,
    loadSeries,
  ]);

  /**
   * Handle series selection
   */
  const handleSeriesSelect = useCallback(
    async (seriesId: string) => {
      if (!seriesId) return;

      // Check if series is already loaded - if so, just switch to it
      const alreadyLoaded = loadedSeries.find((s) => getBaseFilename(s.filename) === seriesId);

      if (alreadyLoaded) {
        // Series already loaded - safe to switch immediately. Re-anchor
        // slice atoms to the new series's dims (loadSeries doesn't run
        // here since it's already cached).
        setSelectedSeriesId(seriesId);
        setSeriesLoadingState("loaded");
        setCurrentSlice(0);
        if (alreadyLoaded.dims) {
          setTotalSlices(alreadyLoaded.dims[0]);
        }
        return;
      }

      // Series not loaded - need to load it. Set loading state FIRST.
      setSeriesLoadingState("loading");

      // Check if DICOM is available
      if (!isDicomAvailable) {
        const errorMsg = "Study DICOM not available";
        setSeriesError(errorMsg);
        setSeriesLoadingState("error");
        return;
      }

      // Check if series exists in metadata
      if (!studyMetadata?.series?.[seriesId]) {
        const errorMsg = `Series ${seriesId} not found`;
        setSeriesError(errorMsg);
        setSeriesLoadingState("error");
        return;
      }

      // Clear any previous error since this series is valid
      setSeriesError(null);

      // Concurrent-call gate. `isStudyLoading` from useAtomValue above is the
      // render snapshot; a rapid double-call between renders would see `false`
      // on both. Read fresh from the jotai store so the second call observes
      // the first call's `setStudyIsLoading(true)` (inside loadSeries) and
      // bails out. Surfaced as a console.warn — silent fallbacks hide real
      // user intent loss.
      const store = getDefaultStore();
      if (store.get(studyIsLoadingAtom)) {
        console.warn(
          `[useDicomSeriesLoader] handleSeriesSelect(${seriesId}) dropped — study already loading.`,
        );
        return;
      }
      try {
        await loadSeries(seriesId);

        // Update selected series ONLY after successful load. loadSeries
        // already wrote totalSlices; reset currentSlice so the freshly
        // selected series starts at the top.
        setSelectedSeriesId(seriesId);
        setSeriesLoadingState("loaded");
        setCurrentSlice(0);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Failed to load series";
        setSeriesError(errorMsg);
        setSeriesLoadingState("error");
      }
    },
    [
      loadedSeries,
      isDicomAvailable,
      studyMetadata,
      loadSeries,
      setCurrentSlice,
      setSelectedSeriesId,
      setSeriesError,
      setSeriesLoadingState,
      setTotalSlices,
    ],
  );

  const setSliceIndex = useCallback((slice: number, totalSlices: number) => {
    return Math.max(0, Math.min(slice, totalSlices - 1));
  }, []);

  // Compute loading stage for progressive UI feedback
  const loadingStage: LoadingStage = useMemo(() => {
    if (isLoadingMetadata) return "fetching-metadata";
    if (!isInitialized) return "initializing-cornerstone";
    if (loadedSeries.length === 0 || seriesLoadingState === "loading") return "loading-frames";
    return "ready";
  }, [isLoadingMetadata, isInitialized, loadedSeries.length, seriesLoadingState]);

  return {
    selectedSeriesId,
    handleSeriesSelect,
    isLoading: isStudyLoading || isLoadingMetadata,
    isFetchingUrls: isLoadingMetadata,
    setSliceIndex,
    isInitialized,
    // DICOM mode state
    metadataError,
    studyNotCanonicalized,
    // The images could not be read at all (e.g. access denied), so the viewer
    // says why instead of waiting on them.
    imagesError:
      metadataQueryError && !studyMetadata && !studyNotCanonicalized
        ? metadataQueryError.message
        : null,
    studyMetadata,
    isDicomAvailable,
    // Loading stage tracking for progressive UI
    loadingStage,
  };
}
